import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Eye, Shield, Sparkles, Camera, Info } from 'lucide-react';
import { useFormContext } from '../contexts/FormContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useCentralizedVehicleInterest } from '../hooks/useCentralizedVehicleInterest';
import { useChatSession } from '../hooks/useChatSession';
import { useDealerSession } from '../contexts/DealerSessionContext';
import { logger } from '../utils/logger';
import { ga4Tracker, extractVehicleData } from '../utils/ga4Tracking';
import { openURL } from '../utils/navigationUtils';

interface VehicleData {
  Main_Picture_URL?: string;
  Year?: number | string;
  Make?: string;
  Model?: string;
  Trim?: string;
  Exterior_Color?: string;
  Price?: number;
  Mileage?: number | string;
  VDP_URL?: string;
  Stock_Number?: string;
  Vehicle_Description_Summary?: string;
  Features?: string[];
  CARFAX_URL?: string;
  VIN?: string;
  Fuel_Type?: string;
  LowestPayment?: string;
  LowestPaymentTerms?: string;
  "vehicle location"?: {
    name?: string;
    phone?: string;
    location?: {
      city?: string;
      province?: string;
      postalCode?: string;
    };
  };
}

interface VehicleCardProps {
  vehicle: VehicleData;
  onDiscoverOffer?: () => void;
}

const VehicleCard: React.FC<VehicleCardProps> = ({ vehicle, onDiscoverOffer }) => {
  const { activateOfferForm, activateCarfaxForm, activeForm, isOfferHighlighted } = useFormContext();
  const { t, language } = useLanguage();
  const { sessionId } = useChatSession();
  const { sessionData } = useDealerSession();
  const { storeVehicleInterest } = useCentralizedVehicleInterest();

  // Debug state tracking removed for production

  const formatPrice = (price: number | undefined) => {
    if (!price) return t('vehicle.price.not.available');
    return new Intl.NumberFormat('fr-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const formatMileage = (mileage: number | string | undefined) => {
    if (!mileage && mileage !== 0) return t('vehicle.mileage.not.available');
    
    // If it's already a string (from API), return it as-is if it contains "km"
    if (typeof mileage === 'string') {
      if (mileage.includes('km')) {
        return mileage;
      }
      // If string doesn't contain "km", add it
      return `${mileage} km`;
    }
    
    // If it's a number, format it with "km"
    return `${mileage.toLocaleString('fr-CA')} km`;
  };

  const formatYear = (year: number | string | undefined) => {
    if (!year) return '';
    return year.toString();
  };

  const translateFuelType = (fuelType: string | undefined) => {
    if (!fuelType) return '';
    
    const lowercased = fuelType.toLowerCase();
    if (lowercased.includes('electric') || lowercased.includes('électrique')) return t('fuel.electric');
    if (lowercased.includes('plugin') || lowercased.includes('phev') || lowercased.includes('rechargeable')) return t('fuel.phev');
    if (lowercased.includes('hybrid') || lowercased.includes('hybride')) return t('fuel.hybrid');
    if (lowercased.includes('gas') || lowercased.includes('gasoline') || lowercased.includes('essence')) return t('fuel.gas');
    if (lowercased.includes('diesel')) return t('fuel.diesel');
    
    // Fallback to original if no translation found
    return fuelType;
  };

  const translateColor = (color: string | undefined) => {
    if (!color) return '';
    
    const lowercased = color.toLowerCase();
    const colorKey = `color.${lowercased}`;
    const translated = t(colorKey);
    
    // If translation key exists, use it, otherwise use original
    return translated !== colorKey ? translated : color;
  };

  const getDisplayModel = () => {
    if (!vehicle.Model) return '';
    if (!vehicle.Trim) return vehicle.Model;
    
    const combinedLength = vehicle.Model.length + vehicle.Trim.length + 1; // +1 for space
    if (combinedLength <= 12) {
      return `${vehicle.Model} ${vehicle.Trim}`;
    }
    return vehicle.Model;
  };

  const shouldShowTrimSeparately = () => {
    if (!vehicle.Model || !vehicle.Trim) return false;
    const combinedLength = vehicle.Model.length + vehicle.Trim.length + 1;
    return combinedLength > 12;
  };

  const isNewVehicle = () => {
    if (vehicle.Mileage === undefined || vehicle.Mileage === null) return false;
    
    // Handle string mileage
    if (typeof vehicle.Mileage === 'string') {
      // Extract numeric value from string like "91,350 km" or "0 km"
      const numericValue = parseFloat(vehicle.Mileage.replace(/[^\d.]/g, ''));
      return numericValue < 50;
    }
    
    // Handle numeric mileage
    return vehicle.Mileage < 50;
  };

  const shouldShowCarfax = () => {
    if (vehicle.Mileage === undefined || vehicle.Mileage === null) return true;
    
    // Handle string mileage
    if (typeof vehicle.Mileage === 'string') {
      // Extract numeric value from string like "91,350 km" or "0 km"
      const numericValue = parseFloat(vehicle.Mileage.replace(/[^\d.]/g, ''));
      return numericValue >= 200;
    }
    
    // Handle numeric mileage
    return vehicle.Mileage >= 200;
  };


  const getPriceDisclaimer = () => {
    return localStorage.getItem('Price_Disclaimer') || '';
  };

  const handleViewClick = async () => {
    // Store vehicle information for tracking
    await storeVehicleInterest(vehicle, {
      sessionId,
      source: 'Site',
      siteUrl: window.location.href
    });
    
    // Track GA4 vehicle view event
    const vehicleData = extractVehicleData(vehicle);
    ga4Tracker.trackVehicleView(vehicleData);
    
    // Navigate to VDP in same tab
    if (vehicle.VDP_URL) {
      window.location.href = getLocalizedVdpUrl(vehicle.VDP_URL);
    }
  };

  const getLocalizedVdpUrl = (url: string): string => {
    if (language === 'fr') {
      let localized = url;
      localized = localized.replace(/\/new\/inventory\//gi, '/neufs/inventaire/');
      localized = localized.replace(/\/used\//gi, '/occasion/');
      localized = localized.replace(/\/new\//gi, '/neuf/');
      return localized;
    }
    return url;
  };

  const handleDetailsClick = async () => {
    // Store vehicle information for tracking
    await storeVehicleInterest(vehicle, {
      sessionId,
      source: 'Site',
      siteUrl: window.location.href
    });
    
    // Track GA4 vehicle view event
    const vehicleData = extractVehicleData(vehicle);
    ga4Tracker.trackVehicleView(vehicleData);
    
    // Navigate to VDP with localized URL
    if (vehicle.VDP_URL) {
      window.location.href = getLocalizedVdpUrl(vehicle.VDP_URL);
    }
  };

  const handleCarfaxClick = async () => {
    // Store vehicle information before opening CARFAX report
    await storeVehicleInterest(vehicle, {
      sessionId,
      source: 'Site',
      siteUrl: window.location.href
    });
    
    // Analytics tracking moved to FormContext to prevent duplicates
    
    if (vehicle.CARFAX_URL) {
      const url = vehicle.CARFAX_URL.startsWith('http') ? vehicle.CARFAX_URL : `https://${vehicle.CARFAX_URL}`;
      await openURL(url);
    }
  };

  const handleCarfaxFormClick = async () => {
    // CRITICAL: Store vehicle data using centralized approach
    const success = await storeVehicleInterest(vehicle, {
      sessionId,
      source: 'Site',
      siteUrl: window.location.href
    });
    
    if (!success) {
      logger.error('Failed to store vehicle data for CARFAX form');
    }
    
    // Analytics tracking moved to FormContext to prevent duplicates
    
    // Pass the VIN to the Carfax form activation
    activateCarfaxForm(vehicle.VIN);
  };

  const handleDiscoverOfferClick = async () => {
    // CRITICAL: Store vehicle data using centralized approach
    const success = await storeVehicleInterest(vehicle, {
      sessionId,
      source: 'Site',
      siteUrl: window.location.href
    });
    
    if (!success) {
      logger.error('Failed to store vehicle data for offer form');
    }
    
    // Analytics tracking moved to FormContext to prevent duplicates
    
    // About to call activateOfferForm
    activateOfferForm();
    
    // ActivateOfferForm called
    
    if (onDiscoverOffer) {
      onDiscoverOffer();
    }
    
    // Discover offer clicked - COMPLETED
  };

  // Check if we have a valid picture URL
  const hasValidPicture = vehicle.Main_Picture_URL && vehicle.Main_Picture_URL.trim() !== '';

  const vehicleImageContent = (
    <div className="mb-4 relative">
      <div 
        className={`${vehicle.VDP_URL ? 'cursor-pointer' : ''}`}
        onClick={vehicle.VDP_URL ? handleDetailsClick : undefined}
      >
        {hasValidPicture ? (
          <div className="relative">
              <img 
                src={vehicle.Main_Picture_URL?.startsWith('http') ? vehicle.Main_Picture_URL : `https://${vehicle.Main_Picture_URL}`}
                alt="Vehicle"
                className="w-full h-48 object-cover rounded-lg d2cbot-bot_border-gray-200"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  const hasRetried = target.getAttribute('data-retry');
                  
                  if (!hasRetried) {
                    target.setAttribute('data-retry', 'true');
                    setTimeout(() => {
                      const originalSrc = target.src;
                      target.src = '';
                      target.src = originalSrc;
                    }, 2000);
                  } else {
                    // Hide the broken image
                    target.style.display = 'none';
                    
                    // Show fallback placeholder
                    const parent = target.parentElement;
                    if (parent) {
                      const fallback = document.createElement('div');
                      fallback.className = 'w-full h-48 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg d2cbot-bot_border-gray-200 flex items-center justify-center text-gray-500 flex-col';
                      fallback.innerHTML = `
                        <svg class="w-8 h-8 mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/>
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"/>
                        </svg>
                        <span class="text-sm font-medium">${t('vehicle.picture.coming.soon')}</span>
                      `;
                      parent.appendChild(fallback);
                    }
                  }
                }}
                onLoad={() => {
                  // Image loaded successfully
                }}
              />
          </div>
        ) : (
          <div className="w-full h-48 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg d2cbot-bot_border-gray-200 flex items-center justify-center text-gray-500 flex-col">
            <Camera className="w-8 h-8 mb-2 text-gray-400" />
            <span className="text-sm font-medium">{t('vehicle.picture.coming.soon')}</span>
          </div>
        )}
      </div>
      
      {/* Top Banner for LowestPayment - Only shown when dealer has payments enabled */}
      {sessionData?.paymentsEnabled && vehicle.LowestPayment && (
        <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-green-500 via-green-600 to-green-500 text-white px-4 py-3 rounded-t-lg shadow-xl animate-fade-in">
          <div className="flex flex-col items-center justify-center gap-1">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 animate-pulse" />
              <span className="text-xl font-extrabold tracking-wide drop-shadow-lg">
                {vehicle.LowestPayment}
              </span>
              <Sparkles className="w-5 h-5 animate-pulse" />
            </div>
            {vehicle.LowestPaymentTerms && (
              <span className="text-xs italic font-normal opacity-90">
                {vehicle.LowestPaymentTerms}
              </span>
            )}
          </div>
        </div>
      )}
      
      {/* Corner Badges Overlay */}
      <div className="absolute top-2 right-2 flex flex-col items-end gap-2">
        {/* Price Tag */}
        {vehicle.Price && (
          <div className="bg-white text-gray-900 text-xs font-semibold px-3 py-1 rounded-full shadow-md border border-gray-200 flex items-center gap-1">
            {formatPrice(vehicle.Price)}
            {getPriceDisclaimer() && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="w-3 h-3 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{getPriceDisclaimer()}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <Card className="w-full max-w-md mx-auto my-4 bg-white shadow-lg d2cbot-bot_border-gray-200">
      <CardContent className="p-4">
        {/* Vehicle Image with Optional Tooltip */}
        {(hasValidPicture || !hasValidPicture) && (
          vehicle.Vehicle_Description_Summary ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="cursor-help">
                    {vehicleImageContent}
                  </div>
                </TooltipTrigger>
                <TooltipContent 
                  className="max-w-xs p-3 text-sm z-50" 
                  side="top" 
                  sideOffset={8}
                  avoidCollisions={true}
                  collisionPadding={16}
                >
                  <p>{vehicle.Vehicle_Description_Summary}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            vehicleImageContent
          )
        )}

        {/* Vehicle Details */}
        <div className="space-y-2 mb-4 text-sm">
          {/* Fuel Type Badge */}
          {vehicle.Fuel_Type && (
            <div className="flex justify-center mb-3">
              <span 
                className={`inline-flex items-center rounded-full text-xs font-semibold px-2.5 py-0.5 border w-fit whitespace-nowrap ${
                  vehicle.Fuel_Type.toLowerCase().includes('electric') || vehicle.Fuel_Type.toLowerCase().includes('électrique')
                    ? 'bg-fuel-electric-bg text-fuel-electric border-fuel-electric'
                    : vehicle.Fuel_Type.toLowerCase().includes('phev') || vehicle.Fuel_Type.toLowerCase().includes('plugin') || vehicle.Fuel_Type.toLowerCase().includes('rechargeable')
                    ? 'bg-fuel-phev-bg text-fuel-phev border-fuel-phev'
                    : vehicle.Fuel_Type.toLowerCase().includes('hybrid') || vehicle.Fuel_Type.toLowerCase().includes('hybride')
                    ? 'bg-fuel-hybrid-bg text-fuel-hybrid border-fuel-hybrid'
                    : 'bg-fuel-gas-bg text-fuel-gas border-fuel-gas'
                }`}
              >
                {translateFuelType(vehicle.Fuel_Type)}
              </span>
            </div>
          )}
          {vehicle.Year && (
            <div className="flex justify-between">
              <span className="font-medium text-gray-700">{t('vehicle.year')}:</span>
              <span className="text-gray-900 font-bold">{formatYear(vehicle.Year)}</span>
            </div>
          )}
          {vehicle.Make && (
            <div className="flex justify-between">
              <span className="font-medium text-gray-700">{t('vehicle.make')}:</span>
              <span className="text-gray-900 font-bold">{vehicle.Make}</span>
            </div>
          )}
          {vehicle.Model && (
            <div className="flex justify-between gap-2 min-w-0">
              <span className="font-medium text-gray-700 flex-shrink-0">{t('vehicle.model')}:</span>
              <span className="text-gray-900 font-bold min-w-0 text-right break-words">{getDisplayModel()}</span>
            </div>
          )}
          {shouldShowTrimSeparately() && (
            <div className="flex justify-between gap-2 min-w-0">
              <span className="font-medium text-gray-700 flex-shrink-0">{t('vehicle.trim')}:</span>
              <span className="text-gray-900 text-right font-bold min-w-0 break-words">{vehicle.Trim}</span>
            </div>
          )}
          {vehicle.Exterior_Color && (
            <div className="flex justify-between gap-2 min-w-0">
              <span className="font-medium text-gray-700 flex-shrink-0">{t('vehicle.color')}:</span>
              <span className="text-gray-900 font-bold text-right min-w-0 break-words">{translateColor(vehicle.Exterior_Color)}</span>
            </div>
          )}

          {vehicle.Mileage !== undefined && vehicle.Mileage !== null && (
            <div className="flex justify-between">
              <span className="font-medium text-gray-700">{t('vehicle.mileage')}:</span>
              <span className="text-gray-900 font-bold">{formatMileage(vehicle.Mileage)}</span>
            </div>
          )}
          {vehicle.Stock_Number && (
            <div className="flex justify-between">
              <span className="font-medium text-gray-700">{t('vehicle.stock')}:</span>
              <span className="text-gray-900 font-bold">{vehicle.Stock_Number}</span>
            </div>
          )}
          {vehicle["vehicle location"] && (
            <div className="mt-3 pt-2 d2cbot-bot_border-t d2cbot-bot_border-gray-200">
              <span className="font-medium text-gray-700 block mb-2">{t('vehicle.location')}:</span>
              <div className="space-y-1 text-sm">
                {vehicle["vehicle location"].name && (
                  <div className="text-gray-900 font-semibold">{vehicle["vehicle location"].name}</div>
                )}
                {vehicle["vehicle location"].location && (
                  <div className="text-gray-600">
                    {vehicle["vehicle location"].location.city && `${vehicle["vehicle location"].location.city}, `}
                    {vehicle["vehicle location"].location.province && `${vehicle["vehicle location"].location.province} `}
                    {vehicle["vehicle location"].location.postalCode}
                  </div>
                )}
                {vehicle["vehicle location"].phone && (
                  <div className="text-gray-600">{vehicle["vehicle location"].phone}</div>
                )}
              </div>
            </div>
          )}
          {vehicle.Features && vehicle.Features.length > 0 && (
            <div className="mt-3 pt-2 d2cbot-bot_border-t d2cbot-bot_border-gray-200">
              <span className="font-medium text-gray-700 block mb-2">{t('vehicle.features')}:</span>
              <div className="flex flex-wrap gap-1">
                {vehicle.Features.map((feature, index) => (
                  <span 
                    key={index}
                    className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full whitespace-nowrap"
                  >
                    {feature}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col gap-2">
          {/* First row: CARFAX + View + Discover Offer */}
          <div className="flex flex-wrap gap-2 gap-y-2 justify-center">
            {/* CARFAX Form Button */}
            {vehicle.VIN && shouldShowCarfax() && (
              <Button
                onClick={handleCarfaxFormClick}
                variant="outline"
                size="sm"
                className="!bg-black d2cbot-bot_border-black !text-white hover:!bg-gray-700 hover:!text-white px-3 py-1 text-xs rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg font-semibold flex items-center gap-1"
              >
                <Shield className="w-3 h-3" />
                {t('vehicle.carfax')}
              </Button>
            )}
            
            {/* CARFAX Report Button */}
            {vehicle.CARFAX_URL && shouldShowCarfax() && (
              <Button
                onClick={handleCarfaxClick}
                variant="outline"
                size="sm"
                className="!bg-black d2cbot-bot_border-black !text-white hover:!bg-gray-700 hover:!text-white px-3 py-1 text-xs rounded-lg transition-colors duration-200 shadow-sm flex items-center gap-1"
              >
                <Shield className="w-3 h-3" />
                {t('vehicle.carfax.report')}
              </Button>
            )}
            
            {/* View Button */}
            {vehicle.VDP_URL && (
              <Button
                onClick={handleViewClick}
                variant="outline"
                size="sm"
                className="!bg-black d2cbot-bot_border-black !text-white hover:!bg-gray-700 hover:!text-white px-3 py-1 text-xs rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg font-semibold flex items-center gap-1"
              >
                <Eye className="w-3 h-3" />
                {t('vehicle.view')}
              </Button>
            )}
            
            {/* Discover Offer Button */}
            <Button
              onClick={handleDiscoverOfferClick}
              size="sm"
              className="!bg-red-600 hover:!bg-red-700 text-white hover:!text-white px-3 py-1 text-xs rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg font-semibold flex items-center gap-1"
            >
              <Sparkles className="w-3 h-3" />
              {t('vehicle.discover.offer')}
            </Button>
          </div>
          
          {/* Second row: Details Button */}
          {vehicle.VDP_URL && (
            <div className="flex justify-center">
              <Button
                onClick={handleDetailsClick}
                variant="outline"
                size="sm"
                className="bg-white d2cbot-bot_border-gray-300 text-black hover:bg-gray-50 px-3 py-1 text-xs rounded-lg transition-colors duration-200 shadow-sm flex items-center gap-1"
              >
                <Eye className="w-3 h-3" />
                {t('vehicle.details')}
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default VehicleCard;
